// Helpers over the draft-07 JSON Schemas the server derives from Rust types (schemars), used by
// SchemaForm: resolving references, defaults for a new value, and switching a tagged union's
// branch. Plain functions, so they are unit-tested without React (schema.test.ts).

import { humanize } from '@/lib/format';

/* eslint-disable @typescript-eslint/no-explicit-any */
export type JsonSchema = Record<string, any>;
export type Obj = Record<string, unknown>;

/** Rust doc comments → plain hint text (drop intra-doc links and backticks). */
export const doc = (d: unknown) => (typeof d === 'string' ? d.replace(/\[`?([^\]`]+)`?\]/g, '$1').replace(/`/g, '') : undefined);

export function resolve(schema: JsonSchema | undefined, root: JsonSchema): JsonSchema {
	let s: JsonSchema = schema ?? {};
	for (let depth = 0; depth < 16; depth++) {
		if (typeof s.$ref === 'string') {
			const name = s.$ref.replace(/^#\/(definitions|\$defs)\//, '');
			const target = root.definitions?.[name] ?? root.$defs?.[name] ?? {};
			const { $ref: _ref, ...rest } = s;
			s = { ...target, ...rest };
			continue;
		}
		if (Array.isArray(s.allOf) && s.allOf.length === 1) {
			const { allOf, ...rest } = s;
			s = { ...resolve(allOf[0], root), ...rest };
			continue;
		}
		return s;
	}
	return s;
}

/** `anyOf: [T, {type: null}]` or `type: [T, 'null']` → T; otherwise undefined. */
export function optionalInner(s: JsonSchema, root: JsonSchema): JsonSchema | undefined {
	if (Array.isArray(s.anyOf) && s.anyOf.length === 2) {
		const nonNull = s.anyOf.filter((b: JsonSchema) => b.type !== 'null');
		if (nonNull.length === 1) return { ...resolve(nonNull[0], root), description: s.description ?? resolve(nonNull[0], root).description };
	}
	if (Array.isArray(s.type) && s.type.includes('null') && s.type.length === 2) {
		return { ...s, type: s.type.find((t: string) => t !== 'null') };
	}
	return undefined;
}

export const constOf = (s: JsonSchema | undefined) => (s?.const ?? (Array.isArray(s?.enum) && s.enum.length === 1 ? s.enum[0] : undefined)) as string | undefined;

/** Branches of a union tagged by a constant `kind` (or any single shared const property). */
export function taggedBranches(s: JsonSchema, root: JsonSchema): { tag: string; branches: JsonSchema[] } | undefined {
	const list = s.oneOf ?? s.anyOf;
	if (!Array.isArray(list) || list.length < 2) return undefined;
	const branches = list.map((b: JsonSchema) => resolve(b, root));
	for (const tag of ['kind', 'protocol', 'type']) {
		if (branches.every((b: JsonSchema) => constOf(b.properties?.[tag]) !== undefined)) return { tag, branches };
	}
	return undefined;
}

/** `oneOf: [{const: 'a', description}, …]` (documented Rust enums) → options. */
export function constOptions(s: JsonSchema): { value: string; label: string }[] | undefined {
	if (Array.isArray(s.enum)) return s.enum.map((v: string) => ({ value: v, label: humanize(String(v)) }));
	const list = s.oneOf;
	if (Array.isArray(list) && list.every((b: JsonSchema) => b.const !== undefined || (Array.isArray(b.enum) && b.enum.length === 1))) {
		return list.map((b: JsonSchema) => {
			const v = constOf(b)!;
			return { value: v, label: b.description ? `${humanize(v)} — ${doc(b.description)}` : humanize(v) };
		});
	}
	return undefined;
}

export function defaultValue(schema: JsonSchema | undefined, root: JsonSchema): unknown {
	const s = resolve(schema, root);
	if (s.default !== undefined) return structuredClone(s.default);
	if (optionalInner(s, root)) return undefined;
	const tagged = taggedBranches(s, root);
	if (tagged) return defaultValue(tagged.branches[0], root);
	const opts = constOptions(s);
	if (opts) return opts[0]?.value;
	const type = Array.isArray(s.type) ? s.type[0] : s.type;
	switch (type) {
		case 'object': {
			const out: Obj = {};
			for (const k of s.required ?? []) {
				const c = constOf(s.properties?.[k]);
				out[k] = c ?? defaultValue(s.properties?.[k], root);
			}
			return out;
		}
		case 'number':
		case 'integer':
			return s.minimum ?? 0;
		case 'boolean':
			return false;
		case 'array':
			return [];
		case 'string':
			return '';
		default:
			return undefined;
	}
}

/** Build a new union branch, carrying over compatible values from the previous one. */
export function switchBranch(prev: Obj, branch: JsonSchema, tag: string, root: JsonSchema): Obj {
	const n = (k: string) => (typeof prev[k] === 'number' ? (prev[k] as number) : undefined);
	const min = n('min') ?? n('low') ?? n('from');
	const max = n('max') ?? n('high') ?? n('to');
	const center = n('mean') ?? n('value') ?? n('start') ?? n('offset') ?? (min !== undefined && max !== undefined ? (min + max) / 2 : 0);
	const span = (max !== undefined && min !== undefined && max > min ? max - min : Math.abs(center) * 0.2) || 1;
	const guess: Record<string, unknown> = {
		mean: center,
		value: center,
		start: center,
		offset: center,
		from: center - span / 2,
		min: center - span / 2,
		low: center - span / 2,
		to: center + span / 2,
		max: center + span / 2,
		high: center + span / 2,
		amplitude: span / 2,
		std: +(span / 10).toPrecision(3),
		sigma: +(span / 20).toPrecision(3),
		stepStdPerS: +(span / 100).toPrecision(3),
		theta: 0.01,
		periodS: 3600,
		duty: 0.5,
		peakHour: 14,
		timezone: 'UTC',
		ratePerHour: 4,
		meanDurationS: 30,
		activeValue: 1,
		idleValue: 0,
		ratePerS: 1,
		jitter: 0,
		phaseDeg: 0,
		// A formula starts as the current level, valid as it is.
		expr: String(+center.toPrecision(6)),
		states: [
			{ value: center - span / 2, label: 'low', meanDwellS: 60 },
			{ value: center + span / 2, label: 'high', meanDwellS: 60 },
		],
	};
	const out: Obj = { [tag]: constOf(branch.properties?.[tag]) };
	for (const [k, prop] of Object.entries<JsonSchema>(branch.properties ?? {})) {
		if (k === tag) continue;
		const required = (branch.required ?? []).includes(k);
		if (prev[k] !== undefined) out[k] = prev[k];
		else if (k in guess && (required || resolve(prop, root).default === undefined)) out[k] = guess[k];
		else if (required) out[k] = defaultValue(prop, root);
		// Optional with a default: shown as the server will apply it (a switch left out reads as off).
		else if (resolve(prop, root).default !== undefined) out[k] = structuredClone(resolve(prop, root).default);
	}
	return out;
}
